import * as React from 'react';
import camelCase from 'lodash/camelCase';
import { ThemeContext } from 'styled-components';

import { ScreenReaderOnly } from '../../Accessibility';
import SVGIcon from '../../Icon/SVGIcon';
import {
  ENTERKEY,
  ENTERKEY_ATL,
  ESCAPE,
  ESCAPE_ATL,
  SPACEBAR,
  SPACEBAR_ATL,
  useOnEscape,
  VALID_DIMENSION_DATA,
  VALID_KEYS,
} from '../Utils';
import { handleKeydown } from '../Utils/events/events';
import { Box } from '../Layout/Box';

import {
  TooltipContainer,
  TooltipPane,
} from './TooltipStyles';

const VALID_KEY_CODES: VALID_KEYS[] = [
  ENTERKEY,
  ENTERKEY_ATL,
  ESCAPE,
  ESCAPE_ATL,
  SPACEBAR,
  SPACEBAR_ATL,
];

export interface IPaneProps {
  width?: VALID_DIMENSION_DATA;
}

export interface ITooltipProps {
  children: React.ReactNode;
  srText: string;
  tooltipId: string;
  paneProps?: IPaneProps;
}

export const TooltipLink: React.FC<ITooltipProps> = ({ children, srText, tooltipId, paneProps, ...rest }) => {
  const { themeTwo: { tooltip } } = React.useContext(ThemeContext);
  const [isDisplayed, toggleDisplay] = React.useState<boolean>(false);
  const id = camelCase(tooltipId);
  const manualActivation = tooltip.activationType === 'manual';

  const togglePane = () => toggleDisplay(!isDisplayed);
  const closePane = () => toggleDisplay(false);
  const openPane = () => toggleDisplay(true);

  const handleValidKeyPress: (e: React.KeyboardEvent<HTMLDivElement>) => void = e => {
    if (VALID_KEY_CODES.includes(e.key)) {
      e.preventDefault();
      e.stopPropagation();

      handleKeydown([ENTERKEY, ENTERKEY_ATL, SPACEBAR, SPACEBAR_ATL])(togglePane)(e);
    }
  };

  useOnEscape(e => {
    e.stopPropagation();
    isDisplayed && closePane();
  });

  const preventDefault = (fn: () => void) => (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault();
    e.stopPropagation();
    fn();
  };

  return (
    <Box
      display='inline-block'
      onMouseEnter={!manualActivation ? openPane : undefined}
      onMouseLeave={!manualActivation ? closePane : undefined}
    >
      <TooltipContainer
        {...rest}
        tabIndex={0}
        aria-controls={id}
        aria-expanded={isDisplayed}
        aria-describedby={isDisplayed ? id : undefined}
        role={manualActivation ? 'button' : undefined}
        onFocus={manualActivation ? undefined : openPane}
        onClick={manualActivation ? preventDefault(togglePane) : undefined}
        onKeyDown={manualActivation ? handleValidKeyPress : undefined}
        onBlur={closePane}
      >
        <ScreenReaderOnly>tooltip information for {srText}</ScreenReaderOnly>
        <SVGIcon icon='tooltip' color={tooltip.icon.color} size={tooltip.icon.size} />
      </TooltipContainer>

      <TooltipPane
        id={id}
        role='tooltip'
        widthProp={paneProps && paneProps.width}
        className={isDisplayed ? 'active' : 'sr-only'}
      >
        {children}
      </TooltipPane>
    </Box>
  );
};

export default TooltipLink;
